import React, { useEffect, useState } from 'react';
import { Modal, List, Rate, Empty, Typography, message, Tag, Button } from 'antd';
import { HeartFilled, HeartOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const { Text } = Typography;

// Catalogo local pra desenhar botoes. Backend mantem espelho.
const TAG_CATALOG = [
  { key: 'faz_gol',       label: 'Faz gol',        emoji: '⚽' },
  { key: 'bom_passe',     label: 'Bom passe',      emoji: '🎯' },
  { key: 'segura_setor',  label: 'Segura o setor', emoji: '🛡️' },
  { key: 'muralha',       label: 'Muralha',        emoji: '🧤' },
  { key: 'corre_muito',   label: 'Corre muito',    emoji: '🏃' },
  { key: 'solidario',     label: 'Solidário',      emoji: '🤝' },
  { key: 'chega_hora',    label: 'Chega na hora',  emoji: '⏰' },
  { key: 'rachao',        label: 'Rachão',         emoji: '🔥' },
];

const RateGameModal = ({ open, game, onClose, onSaved }) => {
  const [teammates, setTeammates] = useState([]);
  const [ratings, setRatings] = useState({});
  const [existing, setExisting] = useState({});
  // endorseTags: { [teammate.id]: Set<tagKey> } — tags marcadas nessa sessao
  const [endorseTags, setEndorseTags] = useState({});
  const [expanded, setExpanded] = useState(null); // teammate.id com painel aberto
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !game) return;
    setRatings({}); setEndorseTags({}); setExpanded(null);
    setLoading(true);
    Promise.all([
      api.get(`/community/player/games/${game.game_id}/teammates`),
      api.get(`/community/player/games/${game.game_id}/my-ratings`).catch(() => ({ data: { ratings: [] } })),
      api.get(`/community/endorsements/mine/game/${game.game_id}`).catch(() => ({ data: { endorsements: {} } })),
    ])
      .then(([tRes, mRes, eRes]) => {
        const mine = {};
        (mRes.data?.ratings || []).forEach(r => { mine[r.rated_player_id] = r.rating; });
        setExisting(mine);
        setRatings(mine);
        const teammates = tRes.data?.teammates || [];
        setTeammates(teammates);
        // Pre-carrega elogios ja dados: mapeia rated_cp_id → teammate.id
        // (players.id, pois endorseTags eh chaveada por teammate.id no UI)
        const byCp = eRes.data?.endorsements || {};
        const marks = {};
        teammates.forEach(t => {
          const tags = t.community_player_id ? byCp[t.community_player_id] : null;
          if (tags && tags.length) marks[t.id] = new Set(tags);
        });
        setEndorseTags(marks);
      })
      .catch(() => message.error('Erro ao carregar jogadores'))
      .finally(() => setLoading(false));
  }, [open, game]);

  const toggleTag = (teammateId, tagKey) => {
    setEndorseTags(prev => {
      const cur = new Set(prev[teammateId] || []);
      if (cur.has(tagKey)) cur.delete(tagKey); else cur.add(tagKey);
      return { ...prev, [teammateId]: cur };
    });
  };

  const submit = async () => {
    const payload = Object.entries(ratings).filter(([, v]) => v > 0).map(([k, v]) => ({
      rated_player_id: parseInt(k, 10), rating: v,
    }));
    if (payload.length === 0) { message.warning('Avalie pelo menos 1 jogador'); return; }
    setSubmitting(true);
    try {
      await api.post(`/community/player/games/${game.game_id}/rate`, { ratings: payload });
      // Elogios: dispara em paralelo, best-effort (nao bloqueia rating)
      const endorseCalls = teammates
        .filter(t => t.community_player_id && (endorseTags[t.id]?.size > 0))
        .map(t => api.post('/community/endorsements', {
          rated_cp_id: t.community_player_id,
          game_id: game.game_id,
          tags: Array.from(endorseTags[t.id]),
        }).catch(() => {}));
      await Promise.all(endorseCalls);
      message.success('Avaliações salvas');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'janela_expirada_24h') message.error('Janela de 24h para avaliar esse jogo já fechou.');
      else if (code === 'janela_nao_aberta') message.error('Avaliação abre 2h após o jogo.');
      else message.error('Erro ao enviar');
    }
    setSubmitting(false);
  };

  const editing = Object.keys(existing).length > 0;

  return (
    <Modal
      title={`${editing ? 'Editar avaliações' : 'Avaliar jogadores'} — ${game?.date_of_game || ''}`}
      open={open}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={submitting}
      okText="Salvar"
      cancelText="Cancelar"
      width={560}
    >
      {loading ? <div style={{ padding: 24, textAlign: 'center' }}>Carregando…</div> :
        teammates.length === 0 ? <Empty description="Nenhum jogador confirmado pra avaliar." /> : (
          <>
            {editing && (
              <div style={{ marginBottom: 12, fontSize: 12, color: '#8c8c8c' }}>
                Você tem <b>24h</b> após o jogo pra ajustar as notas.
              </div>
            )}
            <List
              dataSource={teammates}
              renderItem={(t) => {
                const marks = endorseTags[t.id] || new Set();
                const canEndorse = !!t.community_player_id;
                return (
                  <List.Item style={{ display: 'block', padding: '10px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text>{t.name || `Jogador #${t.id}`}</Text>
                        {existing[t.id] > 0 && <Tag color="blue" style={{ fontSize: 11 }}>já avaliado</Tag>}
                        {marks.size > 0 && (
                          <Tag color="magenta" style={{ fontSize: 11 }}>
                            <HeartFilled /> {marks.size}
                          </Tag>
                        )}
                      </div>
                      <Rate
                        value={ratings[t.id] || 0}
                        onChange={(v) => setRatings(prev => ({ ...prev, [t.id]: v }))}
                      />
                    </div>
                    {canEndorse ? (
                      <div style={{ marginTop: 6 }}>
                        <Button size="small" type="link" style={{ padding: 0, height: 22 }}
                          icon={marks.size > 0 ? <HeartFilled /> : <HeartOutlined />}
                          onClick={() => setExpanded(expanded === t.id ? null : t.id)}>
                          {expanded === t.id ? 'Fechar elogios' : 'Elogiar'}
                        </Button>
                        {expanded === t.id && (
                          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {TAG_CATALOG.map(tag => {
                              const on = marks.has(tag.key);
                              return (
                                <Tag.CheckableTag key={tag.key} checked={on}
                                  onChange={() => toggleTag(t.id, tag.key)}
                                  style={{
                                    padding: '4px 10px', fontSize: 12, borderRadius: 14,
                                    background: on ? '#25D366' : '#f0f0f0',
                                    color: on ? '#fff' : '#333', border: 'none',
                                  }}>
                                  {tag.emoji} {tag.label}
                                </Tag.CheckableTag>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Button size="small" type="link" disabled
                          icon={<HeartOutlined />}
                          style={{ padding: 0, height: 22, opacity: 0.5 }}>
                          Elogiar
                        </Button>
                        <span style={{ fontSize: 11, color: '#8c8c8c', fontStyle: 'italic' }}>
                          Jogador ainda não está cadastrado na comunidade Futezap
                        </span>
                      </div>
                    )}
                  </List.Item>
                );
              }}
            />
          </>
        )
      }
    </Modal>
  );
};

export default RateGameModal;
